import * as Sentry from '@sentry/react';
import axios from 'axios';
import { DBSchema, openDB } from 'idb';
import useSWRImmutable from 'swr/immutable';
import { InvoiceEvidenceView, TransactionView } from '../../types/entity-types';
import { SupportedCountryCurrency } from '../../types/supported-country-currency';
import { fallbackFxData } from './fallback-fx-data';
import { getEvidenceInvoiceDateRange } from './get-evidence-invoice-date-range';
import { getFormattedEarliestDate } from './get-formatted-earliest-date';
import { getFormattedLatestDate } from './get-formatted-latest-date';
import { getFxRatesDateRange } from './get-transactions-date-range';

const FX_API_PATH = '/api/fx';
const IDB_DATABASE_NAME = 'seapoint';
const IDB_STORE_NAME = 'fxRates';

type ExchangeRates = {
  [currency: string]: number;
};

export type DailyRates = {
  [date: string]: ExchangeRates;
};

export type FxData = {
  base: SupportedCountryCurrency;
  rates: DailyRates;
};

type UseFxDataProps = {
  transactions?: TransactionView[];
  evidence?: InvoiceEvidenceView[];
  baseCurrency?: SupportedCountryCurrency;
  shouldFetchData: boolean;
};

export const useFxData = ({ transactions, evidence, baseCurrency, shouldFetchData }: UseFxDataProps) => {
  const transactionDateRange = getFxRatesDateRange({ transactions });
  const evidenceInvoiceDateRange = getEvidenceInvoiceDateRange({ evidence });

  const earliestStartDate = getFormattedEarliestDate({
    dateOne: transactionDateRange.startDate,
    dateTwo: evidenceInvoiceDateRange.startDate,
  });
  const latestEndDate = getFormattedLatestDate({
    dateOne: transactionDateRange.endDate,
    dateTwo: evidenceInvoiceDateRange.endDate,
  });

  const consolidatedDateRange = {
    startDate: earliestStartDate,
    endDate: latestEndDate,
  };

  const shouldFetchFxData = shouldFetchData && !!baseCurrency && !!consolidatedDateRange;

  // Add Sentry breadcrumb when FX data fetching conditions are not met
  if (shouldFetchData && !shouldFetchFxData) {
    Sentry.addBreadcrumb({
      category: 'fx_data',
      message: 'FX data fetching conditions not met',
      level: 'info',
      data: {
        shouldFetchData,
        hasBaseCurrency: !!baseCurrency,
        baseCurrency,
        hasTransactionDateRange: !!transactionDateRange,
        hasEvidenceInvoiceDateRange: !!evidenceInvoiceDateRange,
        hasConsolidatedDateRange: !!consolidatedDateRange,
        transactionDateRange,
        evidenceInvoiceDateRange,
        consolidatedDateRange,
        transactionsCount: transactions?.length || 0,
        evidenceCount: evidence?.length || 0,
      },
    });
  }

  const { data, isLoading, error } = useSWRImmutable<FxData, Error, FetcherParams>(
    shouldFetchFxData
      ? {
          url: `${FX_API_PATH}?start=${consolidatedDateRange.startDate}&end=${consolidatedDateRange.endDate}&base=${baseCurrency}`,
          baseCurrency,
          startDate: consolidatedDateRange.startDate,
          endDate: consolidatedDateRange.endDate,
        }
      : null,
    fetchWithCache,
  );

  // Add Sentry breadcrumb when FX data is missing or has errors
  if (shouldFetchFxData && !isLoading && !data && !error) {
    Sentry.addBreadcrumb({
      category: 'fx_data',
      message: 'FX data is missing without error',
      level: 'warning',
      data: {
        shouldFetchFxData,
        isLoading,
        hasData: !!data,
        hasError: !!error,
        baseCurrency,
        transactionDateRange,
        evidenceInvoiceDateRange,
        consolidatedDateRange,
      },
    });
  }

  if (error) {
    Sentry.addBreadcrumb({
      category: 'fx_data',
      message: 'FX data fetch error',
      level: 'error',
      data: {
        errorMessage: error.message,
        errorType: typeof error,
        baseCurrency,
        transactionDateRange,
        evidenceInvoiceDateRange,
        consolidatedDateRange,
      },
    });
  }

  return {
    data,
    isLoading:
      isLoading ||
      // This is the case where the data is being fetched from the browser storage
      // In these cases, swr doesn't set the loading state because we are not fetching data over the wire
      (shouldFetchFxData && !data && !error),
    error,
  };
};

export type SeapointIndexedDb = DBSchema & {
  fxRates: {
    key: `fxRates-${SupportedCountryCurrency}`;
    value: FxData;
  };
};

type FetcherParams = {
  url: string;
  baseCurrency: SupportedCountryCurrency;
  startDate: string;
  endDate: string;
} | null;

export const fetchWithCache = async ({ url, baseCurrency, startDate, endDate }: NonNullable<FetcherParams>) => {
  try {
    // TODO: move the opening of the db to a context so we have one shared connection per app
    // https://www.npmjs.com/package/idb#examples
    const db = await openDB<SeapointIndexedDb>(IDB_DATABASE_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(IDB_STORE_NAME);
      },
    });

    // Check IndexedDB first
    const cachedData = await db.get(IDB_STORE_NAME, `${IDB_STORE_NAME}-${baseCurrency}`);

    if (cachedData) {
      // Verify cache has the required dates
      const hasStartDate = cachedData.rates?.[startDate];
      const hasEndDate = cachedData.rates?.[endDate];

      if (hasStartDate && hasEndDate) {
        return cachedData;
      }
    }

    // If no cache or missing fx rates for the start or end dates, fetch from API
    const { data } = await axios.get<FxData>(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Store in cache
    await db.put(IDB_STORE_NAME, data, `${IDB_STORE_NAME}-${baseCurrency}`);

    return data;
  } catch {
    Sentry.addBreadcrumb({
      data: {
        message: 'Failed to fetch FX data - using fallback FX rates',
      },
    });

    return fallbackFxData[baseCurrency];
  }
};
